import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatMoney, formatPct } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import {
  Vault,
  ArrowDown,
  ArrowUp,
  ChartLineUp,
  Coins,
  Gear,
  Sparkle,
  ArrowRight,
  Copy,
  ShieldCheck,
  Bank,
  CurrencyBtc,
  SealCheck as CircleWavyCheck,
} from "@phosphor-icons/react";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "holdings", label: "Holdings" },
  { key: "transactions", label: "Transactions" },
  { key: "kyc", label: "KYC" },
  { key: "settings", label: "Settings" },
];

const METHOD_LABEL = {
  bank_wire: "Bank Wire (SWIFT)",
  btc: "Bitcoin",
  usdt: "USDT (ERC-20)",
};

const FALLBACK_METHODS = [
  { key: "bank_wire", label: "Bank Wire", method_type: "bank" },
  { key: "btc", label: "Bitcoin", method_type: "crypto" },
  { key: "usdt", label: "USDT", method_type: "crypto" },
];

const STATUS_TONE = {
  pending: "text-amber-400 border-amber-400/40",
  completed: "text-emerald-400 border-emerald-400/40",
  rejected: "text-red-400 border-red-400/40",
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("overview");
  const [portfolio, setPortfolio] = useState(null);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [p, t] = await Promise.all([
        api.get("/portfolio"),
        api.get("/transactions"),
      ]);
      setPortfolio(p.data);
      setTxs(t.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="dashboard-page" className="text-white">
      <section className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="overline mb-2">Reserved Book</div>
            <h1 className="font-display text-3xl sm:text-4xl">
              Welcome, {user?.name?.split(" ")[0] || "Investor"}.
            </h1>
            <p className="text-sm text-neutral-400 mt-2">
              Your book · {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
          <div className="flex gap-3">
            <DepositButton onDone={() => { load(); refresh(); }} />
            <WithdrawButton onDone={() => { load(); refresh(); }} user={user} />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`dash-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-sm border-b-2 whitespace-nowrap transition-colors ${
                tab === t.key
                  ? "border-[#E6C998] text-[#E6C998]"
                  : "border-transparent text-neutral-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="dash-tab-admin"
              className="ml-auto text-sm text-[#E6C998] flex items-center gap-1 px-3 py-3"
            >
              <ShieldCheck size={16} weight="duotone" /> Admin Console
            </Link>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {loading ? (
          <div className="text-neutral-500 text-sm">Loading your book…</div>
        ) : tab === "overview" ? (
          <Overview user={user} portfolio={portfolio} txs={txs} />
        ) : tab === "holdings" ? (
          <Holdings portfolio={portfolio} />
        ) : tab === "transactions" ? (
          <Transactions txs={txs} />
        ) : tab === "kyc" ? (
          <KycPanel user={user} onDone={refresh} />
        ) : (
          <Settings />
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, sub, tone = "neutral", testid }) {
  const toneMap = {
    gold: "text-[#E6C998]",
    up: "text-emerald-400",
    down: "text-red-400",
    neutral: "text-white",
  };
  return (
    <div className="border border-white/10 bg-[#141414] p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-neutral-500 mb-3">{label}</div>
      <div className={`font-display text-3xl num ${toneMap[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-2 num">{sub}</div>}
    </div>
  );
}

function Overview({ user, portfolio, txs }) {
  const p = portfolio || { total_value: 0, total_invested: 0, total_pl: 0, pl_percent: 0, holdings: [] };
  const tone = p.total_pl >= 0 ? "up" : "down";
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          label="Cash Balance"
          value={formatMoney(user?.balance || 0)}
          sub={user?.reserved_balance > 0 ? `${formatMoney(user.reserved_balance)} reserved` : "Ready to deploy"}
          tone="gold"
          testid="stat-balance"
        />
        <StatCard
          label="Total Invested"
          value={formatMoney(p.total_invested)}
          sub={`${p.holdings.length} positions`}
          testid="stat-invested"
        />
        <StatCard
          label="Portfolio Value"
          value={formatMoney(p.total_value)}
          sub="Live marks"
          testid="stat-value"
        />
        <StatCard
          label="Unrealised P&L"
          value={formatMoney(p.total_pl)}
          sub={formatPct(p.pl_percent)}
          tone={tone}
          testid="stat-pnl"
        />
      </div>

      {user?.is_disabled && !user?.has_deposited && (
        <div className="border border-[#E6C998]/40 bg-[#141414] p-5 flex items-center gap-4">
          <Sparkle size={22} weight="duotone" className="text-[#E6C998]" />
          <div className="text-sm">
            <span className="text-[#E6C998] font-medium">Inclusion bonus armed:</span>{" "}
            Your first deposit will be credited with a +5% welcome bonus once confirmed.
          </div>
        </div>
      )}

      {user?.kyc_status !== "approved" && (
        <div className="border border-amber-400/30 bg-[#141414] p-5 flex items-center gap-4">
          <ShieldCheck size={22} weight="duotone" className="text-amber-400" />
          <div className="text-sm text-neutral-300">
            <span className="text-amber-400 font-medium">KYC {user?.kyc_status || "required"}:</span>{" "}
            Withdrawals are locked until your identity is verified. Submit your details in the KYC tab.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-white/10 bg-[#141414] p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="overline flex items-center gap-2">
              <ChartLineUp size={16} weight="duotone" /> Top Positions
            </div>
            <Link to="/marketplace/crypto" className="text-xs text-[#E6C998] flex items-center gap-1">
              Explore <ArrowRight size={12} />
            </Link>
          </div>
          {p.holdings.length === 0 ? (
            <EmptyHoldings />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-500 uppercase text-xs tracking-wider">
                  <th className="pb-3">Asset</th>
                  <th className="pb-3 text-right">Units</th>
                  <th className="pb-3 text-right">Value</th>
                  <th className="pb-3 text-right">P&L</th>
                </tr>
              </thead>
              <tbody>
                {p.holdings.slice(0, 5).map((h) => (
                  <tr key={h.asset_symbol} className="border-t border-white/5">
                    <td className="py-3">
                      <div className="font-medium">{h.asset_name}</div>
                      <div className="text-xs text-neutral-500">{h.asset_symbol}</div>
                    </td>
                    <td className="py-3 text-right num">{h.units}</td>
                    <td className="py-3 text-right num">{formatMoney(h.current_value)}</td>
                    <td className={`py-3 text-right num ${h.pl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {formatMoney(h.pl)} <span className="text-xs">({formatPct(h.pl_percent)})</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border border-white/10 bg-[#141414] p-6">
          <div className="overline mb-6 flex items-center gap-2">
            <Coins size={16} weight="duotone" /> Recent Activity
          </div>
          {txs.length === 0 ? (
            <div className="text-sm text-neutral-500">No transactions yet.</div>
          ) : (
            <ul className="space-y-4">
              {txs.slice(0, 6).map((t) => (
                <li key={t.id} className="flex justify-between text-sm border-b border-white/5 pb-3 last:border-b-0">
                  <div>
                    <div className="capitalize">
                      {t.type}
                      {t.asset_symbol ? ` · ${t.asset_symbol}` : ""}
                    </div>
                    <div className="text-xs text-neutral-500">
                      {new Date(t.created_at).toLocaleString()}
                    </div>
                    <div className={`inline-block mt-1 text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS_TONE[t.status] || ""}`}>
                      {t.status}
                    </div>
                  </div>
                  <div className={`num ${t.type === "withdraw" ? "text-red-400" : "text-[#E6C998]"}`}>
                    {t.type === "withdraw" ? "-" : "+"}
                    {formatMoney(t.amount)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyHoldings() {
  return (
    <div className="border border-dashed border-white/10 p-10 text-center">
      <Vault size={40} weight="duotone" className="text-[#E6C998] mx-auto mb-4" />
      <div className="font-display text-xl mb-2">Your book is still resting.</div>
      <div className="text-sm text-neutral-400 mb-6">
        Deposit funds and place your first allocation to activate the vault.
      </div>
      <Link to="/marketplace/crypto" className="btn-gold inline-block px-6 py-3 rounded-none text-sm">
        Browse assets
      </Link>
    </div>
  );
}

function Holdings({ portfolio }) {
  const p = portfolio || { holdings: [] };
  if (p.holdings.length === 0) return <EmptyHoldings />;
  return (
    <div className="border border-white/10 bg-[#141414] overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]">
        <thead>
          <tr className="text-left text-neutral-500 uppercase text-xs tracking-wider">
            <th className="p-5">Asset</th>
            <th className="p-5 text-right">Units</th>
            <th className="p-5 text-right">Avg Cost</th>
            <th className="p-5 text-right">Price</th>
            <th className="p-5 text-right">Invested</th>
            <th className="p-5 text-right">Value</th>
            <th className="p-5 text-right">P&L</th>
          </tr>
        </thead>
        <tbody>
          {p.holdings.map((h) => (
            <tr key={h.asset_symbol} data-testid={`holding-${h.asset_symbol}`} className="border-t border-white/5">
              <td className="p-5">
                <div className="font-medium">{h.asset_name}</div>
                <div className="text-xs text-neutral-500">{h.asset_symbol} · {h.category}</div>
              </td>
              <td className="p-5 text-right num">{h.units}</td>
              <td className="p-5 text-right num">{formatMoney(h.avg_cost, { max: 4 })}</td>
              <td className="p-5 text-right num">{formatMoney(h.current_price)}</td>
              <td className="p-5 text-right num">{formatMoney(h.invested)}</td>
              <td className="p-5 text-right num">{formatMoney(h.current_value)}</td>
              <td className={`p-5 text-right num ${h.pl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {formatMoney(h.pl)}
                <div className="text-xs">{formatPct(h.pl_percent)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Transactions({ txs }) {
  if (txs.length === 0)
    return (
      <div className="border border-dashed border-white/10 p-10 text-center text-neutral-400 text-sm">
        No transactions yet.
      </div>
    );
  return (
    <div className="border border-white/10 bg-[#141414] overflow-x-auto">
      <table className="w-full text-sm min-w-[800px]">
        <thead>
          <tr className="text-left text-neutral-500 uppercase text-xs tracking-wider">
            <th className="p-5">When</th>
            <th className="p-5">Type</th>
            <th className="p-5">Reference</th>
            <th className="p-5">Details</th>
            <th className="p-5 text-right">Amount</th>
            <th className="p-5 text-right">Bonus</th>
            <th className="p-5">Status</th>
          </tr>
        </thead>
        <tbody>
          {txs.map((t) => (
            <tr key={t.id} data-testid={`tx-row-${t.id}`} className="border-t border-white/5 align-top">
              <td className="p-5 text-neutral-400 whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
              <td className="p-5 capitalize">{t.type}</td>
              <td className="p-5 text-xs num text-neutral-400">{t.reference || "—"}</td>
              <td className="p-5 text-xs text-neutral-400">
                {t.asset_name && <div>{t.asset_name}</div>}
                {t.method && <div>{METHOD_LABEL[t.method]}</div>}
                {t.destination && <div className="truncate max-w-[220px]">→ {t.destination}</div>}
                {t.rejection_reason && <div className="text-red-400">Reason: {t.rejection_reason}</div>}
              </td>
              <td className="p-5 text-right num">{formatMoney(t.amount)}</td>
              <td className="p-5 text-right num text-[#E6C998]">
                {t.bonus ? `+${formatMoney(t.bonus)}` : (t.bonus_preview ? `~${formatMoney(t.bonus_preview)}` : "—")}
              </td>
              <td className="p-5">
                <span className={`text-xs uppercase tracking-wider px-2 py-1 border ${STATUS_TONE[t.status] || ""}`}>
                  {t.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KycPanel({ user, onDone }) {
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState({
    full_name: user?.name || "",
    date_of_birth: "",
    country: "",
    document_type: "passport",
    document_number: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/kyc/me").then((r) => setRecord(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/kyc/submit", form);
      toast.success("KYC submitted — pending review");
      const r = await api.get("/kyc/me");
      setRecord(r.data);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const status = record?.status || user?.kyc_status || "not_submitted";

  return (
    <div className="max-w-2xl border border-white/10 bg-[#141414] p-8">
      <div className="overline mb-4 flex items-center gap-2">
        <ShieldCheck size={16} weight="duotone" /> Identity verification
      </div>
      <div className={`mb-6 inline-block text-xs uppercase tracking-wider px-3 py-1 border ${
        status === "approved" ? "text-emerald-400 border-emerald-400/40"
        : status === "rejected" ? "text-red-400 border-red-400/40"
        : status === "pending" ? "text-amber-400 border-amber-400/40"
        : "text-neutral-400 border-white/20"
      }`}>
        Status · {status}
      </div>

      {status === "approved" ? (
        <div className="text-neutral-300 text-sm flex items-start gap-3">
          <CircleWavyCheck size={22} weight="duotone" className="text-emerald-400" />
          Your identity is verified. Withdrawals are unlocked.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="kyc-form">
          <Field label="Legal full name">
            <input required data-testid="kyc-name" value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="lux-input" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Date of birth (YYYY-MM-DD)">
              <input required data-testid="kyc-dob" placeholder="1990-05-14"
                value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                className="lux-input" />
            </Field>
            <Field label="Country of residence">
              <input required data-testid="kyc-country" value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="lux-input" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Document type">
              <select data-testid="kyc-doctype" value={form.document_type}
                onChange={(e) => setForm({ ...form, document_type: e.target.value })}
                className="lux-input">
                <option value="passport">Passport</option>
                <option value="id_card">National ID</option>
                <option value="drivers_license">Driver's licence</option>
              </select>
            </Field>
            <Field label="Document number">
              <input required data-testid="kyc-docnumber" value={form.document_number}
                onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                className="lux-input" />
            </Field>
          </div>
          <button
            type="submit"
            data-testid="kyc-submit"
            disabled={busy}
            className="btn-gold px-6 py-3 rounded-none text-sm disabled:opacity-60"
          >
            {busy ? "Submitting…" : status === "rejected" ? "Resubmit KYC" : "Submit for review"}
          </button>
          {record?.note && (
            <div className="text-xs text-red-400">Reviewer note: {record.note}</div>
          )}
          <p className="text-xs text-neutral-500">
            Documents are stored under our privacy policy and only accessed by
            the compliance desk.
          </p>
        </form>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">{label}</span>
      {children}
    </label>
  );
}

function Settings() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [isDis, setIsDis] = useState(!!user?.is_disabled);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/account", { name, is_disabled: isDis });
      await refresh();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl border border-white/10 bg-[#141414] p-8">
      <div className="overline mb-4 flex items-center gap-2">
        <Gear size={16} weight="duotone" /> Account settings
      </div>
      <div className="space-y-6">
        <Field label="Full Name">
          <input data-testid="settings-name" value={name}
            onChange={(e) => setName(e.target.value)} className="lux-input" />
        </Field>
        <Field label="Email">
          <input value={user?.email || ""} disabled
            className="w-full bg-black border border-white/10 text-neutral-500 px-4 py-3 cursor-not-allowed" />
        </Field>
        <label className="flex items-start gap-3 p-4 border border-[#E6C998]/20 cursor-pointer">
          <input data-testid="settings-inclusion" type="checkbox" checked={isDis}
            onChange={(e) => setIsDis(e.target.checked)} className="mt-1 accent-[#E6C998]" />
          <div>
            <div className="text-[#E6C998] text-sm font-medium">Inclusion status</div>
            <div className="text-xs text-neutral-400 mt-1">
              I identify as a person living with a disability. Unlocks 5% bonus on first
              deposit (if not yet claimed).
            </div>
          </div>
        </label>
        <button data-testid="settings-save" onClick={save} disabled={saving}
          className="btn-gold px-6 py-3 rounded-none text-sm disabled:opacity-60">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

/* ----------------------------- Deposit ------------------------------ */
function DepositButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState("bank_wire");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [instructions, setInstructions] = useState(null);
  const [methods, setMethods] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/payment-methods")
      .then((r) => {
        setMethods(r.data);
        if (r.data.length && !r.data.find((m) => m.key === method))
          setMethod(r.data[0].key);
      })
      .catch(() => setMethods([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    setOpen(false);
    setInstructions(null);
    setAmount("");
    setMethod("bank_wire");
  };

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    setBusy(true);
    try {
      const { data } = await api.post("/transactions/deposit", { amount: amt, method });
      setInstructions({ ...data.payment_instructions, tx: data.transaction });
      toast.success("Deposit request created · Pending payment");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = (v) => {
    navigator.clipboard.writeText(v);
    toast.success("Copied");
  };

  return (
    <>
      <button
        data-testid="deposit-open"
        onClick={() => setOpen(true)}
        className="btn-gold px-5 py-3 rounded-none text-sm flex items-center gap-2"
      >
        <ArrowDown size={16} weight="bold" /> Deposit
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#E6C998]/30 w-full max-w-lg p-8">
            {!instructions ? (
              <>
                <div className="overline mb-2">New Deposit · Pending</div>
                <h3 className="font-display text-2xl mb-6">Fund your reserved book</h3>

                <div className={`grid gap-2 mb-5 ${methods.length > 3 ? "grid-cols-4" : "grid-cols-3"}`}>
                  {(methods.length ? methods : FALLBACK_METHODS).map((m) => {
                    const Icon = m.method_type === "crypto" ? CurrencyBtc : m.method_type === "bank" ? Bank : Coins;
                    return (
                      <button
                        key={m.key}
                        data-testid={`deposit-method-${m.key}`}
                        onClick={() => setMethod(m.key)}
                        className={`p-3 text-xs border flex flex-col items-center gap-1 transition-colors ${
                          method === m.key
                            ? "border-[#E6C998] text-[#E6C998] bg-[#141414]"
                            : "border-white/15 text-neutral-400 hover:text-white"
                        }`}
                      >
                        <Icon size={20} weight="duotone" />
                        {m.label}
                      </button>
                    );
                  })}
                </div>

                <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">Amount (USD)</label>
                <input
                  data-testid="deposit-amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder="1000.00"
                  className="lux-input num"
                  autoFocus
                />

                <div className="flex gap-3 mt-6">
                  <button onClick={close} className="btn-outline-gold flex-1 py-3 rounded-none text-sm">Cancel</button>
                  <button
                    onClick={submit}
                    disabled={busy}
                    data-testid="deposit-submit"
                    className="btn-gold flex-1 py-3 rounded-none text-sm disabled:opacity-60"
                  >
                    {busy ? "Creating…" : "Get instructions"}
                  </button>
                </div>
                <p className="text-xs text-neutral-500 mt-5">
                  A pending transaction is created. Funds credit only after the
                  desk confirms receipt.
                </p>
              </>
            ) : (
              <>
                <div className="overline mb-2">Payment Instructions</div>
                <h3 className="font-display text-2xl mb-2">Complete your transfer</h3>
                <p className="text-xs text-neutral-500 mb-6">
                  ETA: {instructions.eta}. Include the reference exactly.
                </p>

                <div className="space-y-3 mb-6">
                  <InstructionRow label="Reference" value={instructions.reference} onCopy={copy} testid="deposit-ref" />
                  {Object.entries(instructions).filter(([k]) =>
                    !["label", "eta", "reference", "tx"].includes(k)
                  ).map(([k, v]) => (
                    <InstructionRow key={k} label={k.replace(/_/g, " ")} value={String(v)} onCopy={copy} />
                  ))}
                </div>

                <button
                  onClick={close}
                  data-testid="deposit-close"
                  className="btn-gold w-full py-3 rounded-none text-sm"
                >
                  Done · Go to transactions
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function InstructionRow({ label, value, onCopy, testid }) {
  return (
    <div className="border border-white/10 bg-[#141414] p-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{label}</div>
        <div data-testid={testid} className="text-sm num text-white break-all">{value}</div>
      </div>
      <button
        onClick={() => onCopy(value)}
        className="text-[#E6C998] p-1 shrink-0"
        aria-label={`Copy ${label}`}
      >
        <Copy size={16} weight="duotone" />
      </button>
    </div>
  );
}

/* ----------------------------- Withdraw ------------------------------ */
function WithdrawButton({ onDone, user }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_wire");
  const [destination, setDestination] = useState("");
  const [beneficiary, setBeneficiary] = useState("");
  const [busy, setBusy] = useState(false);
  const [methods, setMethods] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/payment-methods")
      .then((r) => {
        setMethods(r.data);
        if (r.data.length && !r.data.find((m) => m.key === method))
          setMethod(r.data[0].key);
      })
      .catch(() => setMethods([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const kycBlocked = user?.kyc_status !== "approved";

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    if (!destination || destination.length < 4)
      return toast.error("Enter a valid destination");
    setBusy(true);
    try {
      await api.post("/transactions/withdraw", {
        amount: amt, method, destination,
        beneficiary_name: beneficiary || null,
      });
      toast.success("Withdrawal requested · Pending admin review");
      setAmount(""); setDestination(""); setBeneficiary("");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="withdraw-open"
        onClick={() => setOpen(true)}
        className="btn-outline-gold px-5 py-3 rounded-none text-sm flex items-center gap-2"
      >
        <ArrowUp size={16} weight="bold" /> Withdraw
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#E6C998]/30 w-full max-w-lg p-8">
            <div className="overline mb-2">Request Withdrawal</div>
            <h3 className="font-display text-2xl mb-2">Send funds to your account</h3>
            <p className="text-xs text-neutral-500 mb-6">
              Funds are reserved immediately and released after the desk approves.
            </p>

            {kycBlocked && (
              <div className="border border-amber-400/40 bg-[#141414] p-3 mb-5 text-xs text-amber-400">
                KYC not approved — please complete the KYC tab before withdrawing.
              </div>
            )}

            <div className={`grid gap-2 mb-5 ${methods.length > 3 ? "grid-cols-4" : "grid-cols-3"}`}>
              {(methods.length ? methods : FALLBACK_METHODS).map((m) => {
                const Icon = m.method_type === "crypto" ? CurrencyBtc : m.method_type === "bank" ? Bank : Coins;
                return (
                  <button
                    key={m.key}
                    data-testid={`withdraw-method-${m.key}`}
                    onClick={() => setMethod(m.key)}
                    className={`p-3 text-xs border flex flex-col items-center gap-1 ${
                      method === m.key
                        ? "border-[#E6C998] text-[#E6C998] bg-[#141414]"
                        : "border-white/15 text-neutral-400 hover:text-white"
                    }`}
                  >
                    <Icon size={20} weight="duotone" />
                    {m.label}
                  </button>
                );
              })}
            </div>

            <div className="space-y-4">
              <Field label="Amount (USD)">
                <input data-testid="withdraw-amount" value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder="1000.00" className="lux-input num" />
              </Field>
              <Field label={method === "bank_wire" ? "IBAN or Account number" : "Destination address"}>
                <input data-testid="withdraw-destination" value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="lux-input" />
              </Field>
              {method === "bank_wire" && (
                <Field label="Beneficiary name">
                  <input data-testid="withdraw-beneficiary" value={beneficiary}
                    onChange={(e) => setBeneficiary(e.target.value)}
                    className="lux-input" />
                </Field>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setOpen(false)} className="btn-outline-gold flex-1 py-3 rounded-none text-sm">Cancel</button>
              <button
                data-testid="withdraw-submit"
                onClick={submit}
                disabled={busy || kycBlocked}
                className="btn-gold flex-1 py-3 rounded-none text-sm disabled:opacity-60"
              >
                {busy ? "Submitting…" : "Request withdrawal"}
              </button>
            </div>
            <p className="text-xs text-neutral-500 mt-5">
              The requested amount is reserved from your available balance until
              the desk approves or rejects.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
